/**
 * Prüfen, Vorschau und Speichern der Einstellungen. Die Nutzlasten sind die des Serververtrags
 * (`PUT …/pricing-configuration`, `POST …/preview`); `previewedAt` bleibt im Client. Jede Antwort
 * zu einem Apartment, das nicht mehr gewählt ist, wird verworfen (`still`).
 */
import type { QueryClient } from '@tanstack/react-query';
import type {
  Api,
  ApiError,
  PricingConfigurationInput,
  PricingConfigurationPreviewResponse,
  PricingConfigurationResponse,
  PricingConfigurationValidationResponse,
  PricingOwnershipAdoptionRequest,
} from '@/shared/api';
import { toApiError } from '@/shared/api';
import { NOT_SAVED } from './configurationProblemText';
import type { SavedOutcome } from './releaseRailModel';

/**
 * `rejected`: die Prüfung des Servers hat den Entwurf abgelehnt, ihre Befunde stehen in `validation`.
 * `unknown`: das Speichern blieb ohne Antwort – erst der neu geladene Stand zeigt, was gilt.
 */
export type SaveResult = SavedOutcome | 'rejected' | 'unknown' | false;
export type Busy = 'validating' | 'previewing' | 'saving' | null;

interface Deps {
  api: Api;
  queryClient: QueryClient;
  propertyId?: string;
  base?: PricingConfigurationResponse;
  /** Lädt den Stand neu; ohne Antwort `undefined`. */
  refetch: () => Promise<PricingConfigurationResponse | undefined>;
  still: (id: string) => boolean;
  begin: (state: Busy) => void;
  end: () => void;
  setValidation: (value: PricingConfigurationValidationResponse | undefined) => void;
  setCheckError: (problem: ApiError) => void;
  setPreviewError: (problem: ApiError) => void;
  setSaveError: (problem: ApiError | undefined) => void;
  setSaved: (outcome: SavedOutcome) => void;
}

export function configurationRequests(d: Deps) {
  const failed = async (id: string, error: unknown, set: (problem: ApiError) => void) => {
    const problem = await toApiError(error);
    if (!d.still(id)) return undefined;
    set(problem);
    // Ein veraltetes Profil wählt man nur aus einer frischen Liste neu.
    if (problem.title === 'pricing_strategy_profile_version_conflict') {
      void d.queryClient.invalidateQueries({ queryKey: ['pricing-strategy-profiles'] });
    }
    return problem;
  };

  /** Gleiche Version: nichts gespeichert. Eine neue meldet das Formular (`useConfigurationReset`). */
  const settleUnknown = async (id: string, base: PricingConfigurationResponse) => {
    const fresh = await d.refetch();
    if (!fresh || !d.still(id)) return;
    d.setSaveError(fresh.versionId === base.versionId ? NOT_SAVED : undefined);
  };

  /** Lesende Serverprüfung vor „Wirkung prüfen“; ein älterer Befund gilt danach nicht mehr. */
  const validate = async (input: PricingConfigurationInput) => {
    if (!d.propertyId) return undefined;
    const id = d.propertyId;
    d.begin('validating');
    d.setValidation(undefined);
    try {
      const result = await d.api.pricingConfigurations.validate(id, input);
      if (!d.still(id)) return undefined;
      d.setValidation(result);
      return result;
    } catch (error) {
      await failed(id, error, d.setCheckError);
      return undefined;
    } finally {
      d.end();
    }
  };

  const preview = async (
    input: PricingConfigurationInput,
  ): Promise<PricingConfigurationPreviewResponse | undefined> => {
    if (!d.propertyId || !d.base) return undefined;
    const id = d.propertyId;
    d.begin('previewing');
    try {
      const result = await d.api.pricingConfigurations.preview(id, {
        expectedCurrentVersionId: d.base.versionId,
        configuration: input,
      });
      return d.still(id) ? result : undefined;
    } catch (error) {
      await failed(id, error, d.setPreviewError);
      return undefined;
    } finally {
      d.end();
    }
  };

  const save = async (
    input: PricingConfigurationInput,
    previewAttestationId?: string,
    ownershipAdoption?: PricingOwnershipAdoptionRequest,
    previewedAt?: string,
  ): Promise<SaveResult> => {
    const base = d.base;
    if (!d.propertyId || !base) return false;
    const id = d.propertyId;
    d.begin('saving');
    let sent = false;
    try {
      const checked = await d.api.pricingConfigurations.validate(id, input);
      if (!d.still(id)) return false;
      d.setValidation(checked);
      if (!checked.canSavePaused) return 'rejected';
      sent = true;
      const updated = await d.api.pricingConfigurations.update(id, {
        expectedCurrentVersionId: base.versionId,
        configuration: input,
        ...(previewAttestationId ? { previewAttestationId } : {}),
        ...(ownershipAdoption ? { ownershipAdoption } : {}),
      });
      if (!d.still(id)) return false;
      const outcome: SavedOutcome = {
        versionNumber: updated.versionNumber,
        previousVersionNumber: base.versionNumber,
        mode: updated.configuration.mode,
        previousMode: base.configuration.mode,
        previewedAt: previewedAt ?? null,
      };
      d.setSaved(outcome);
      d.queryClient.setQueryData(['pricing-configuration', id], updated);
      await d.queryClient.invalidateQueries({ queryKey: ['pricing-configuration', id] });
      return outcome;
    } catch (error) {
      // Scheitert schon die Prüfung, ist sicher nichts gespeichert.
      if (!sent) {
        await failed(id, error, d.setCheckError);
        return false;
      }
      const problem = await failed(id, error, d.setSaveError);
      // Keine Antwort ist kein Nein: ob gespeichert wurde, zeigt erst der neu geladene Stand.
      if (problem?.status !== 0) return false;
      void settleUnknown(id, base);
      return 'unknown';
    } finally {
      d.end();
    }
  };

  return { validate, preview, save };
}
